import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  min-width: 500px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-gray-900);
`;

const ReasonOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius);
  cursor: pointer;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-gray-50);
    border-color: var(--color-gray-300);
  }

  &:has(input:checked) {
    background: var(--color-blue-50);
    border-color: var(--color-primary);
  }
`;

const Radio = styled.input`
  margin-top: 0.125rem;
`;

const ReasonText = styled.div`
  flex: 1;
`;

const ReasonLabel = styled.div`
  font-weight: 500;
  color: var(--color-gray-900);
  margin-bottom: 0.25rem;
`;

const ReasonDescription = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius);
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 1px var(--color-primary);
  }

  &:disabled {
    background: var(--color-gray-100);
    color: var(--color-gray-500);
  }
`;

const Actions = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--color-gray-200);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => {
    switch (props.$variant) {
      case 'danger':
        return `
          background: var(--color-red-600);
          border-color: var(--color-red-600);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-red-700);
            border-color: var(--color-red-700);
          }
        `;
      case 'primary':
        return `
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }
        `;
      default:
        return `
          background: white;
          border-color: var(--color-gray-300);
          color: var(--color-gray-700);
          &:hover:not(:disabled) {
            background: var(--color-gray-50);
            border-color: var(--color-gray-400);
          }
        `;
    }
  }}
`;

const PREDEFINED_REASONS = [
  {
    id: 'inappropriate_content',
    label: 'Inappropriate Content',
    description: 'Contains violence, sexual content, or other material unsuitable for children'
  },
  {
    id: 'hate_speech',
    label: 'Hate Speech or Discrimination',
    description: 'Contains discriminatory language or promotes hatred against individuals or groups'
  },
  {
    id: 'spam_low_quality',
    label: 'Spam or Low Quality',
    description: 'Story appears to be spam, gibberish, or extremely low quality content'
  },
  {
    id: 'copyright_violation',
    label: 'Copyright Violation',
    description: 'Story appears to infringe on copyrighted material without permission'
  },
  {
    id: 'misleading_harmful',
    label: 'Misleading or Harmful Information',
    description: 'Contains false information or content that could be harmful to children'
  },
  {
    id: 'privacy_violation',
    label: 'Privacy Violation',
    description: 'Contains personal information that should not be shared publicly'
  },
  {
    id: 'custom',
    label: 'Other (Custom Reason)',
    description: 'Provide a custom reason for rejection'
  }
];

export interface RejectReasonModalProps {
  isOpen: boolean;
  storyTitle: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  isOpen,
  storyTitle,
  onConfirm,
  onCancel
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    let finalReason = '';
    
    if (selectedReason === 'custom') {
      finalReason = customReason.trim();
    } else {
      const reason = PREDEFINED_REASONS.find(r => r.id === selectedReason);
      finalReason = reason ? reason.label : '';
    }

    if (!finalReason) {
      return; // Don't allow empty reason
    }

    onConfirm(finalReason);
    
    // Reset form
    setSelectedReason('');
    setCustomReason('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setCustomReason('');
    onCancel();
  };

  const isFormValid = selectedReason && (selectedReason !== 'custom' || customReason.trim());

  return (
    <Overlay onClick={handleCancel}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <span>❌</span>
            Reject Story
          </Title>
        </Header>
        <Content>
          <Section>
            <p style={{ margin: '0 0 1rem 0', color: 'var(--color-gray-700)' }}>
              You are about to reject the story: <strong>"{storyTitle}"</strong>
            </p>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--color-gray-600)', fontSize: '0.875rem' }}>
              Please select a reason for rejection. This will help the author understand what needs to be improved.
            </p>
          </Section>

          <Section>
            <SectionTitle>Rejection Reason</SectionTitle>
            {PREDEFINED_REASONS.map(reason => (
              <ReasonOption key={reason.id}>
                <Radio
                  type="radio"
                  name="rejectReason"
                  value={reason.id}
                  checked={selectedReason === reason.id}
                  onChange={(e) => setSelectedReason(e.target.value)}
                />
                <ReasonText>
                  <ReasonLabel>{reason.label}</ReasonLabel>
                  <ReasonDescription>{reason.description}</ReasonDescription>
                </ReasonText>
              </ReasonOption>
            ))}
          </Section>

          {selectedReason === 'custom' && (
            <Section>
              <SectionTitle>Custom Reason</SectionTitle>
              <TextArea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Please provide a detailed reason for rejecting this story..."
                maxLength={500}
              />
              <div style={{ 
                textAlign: 'right', 
                fontSize: '0.75rem', 
                color: 'var(--color-gray-500)',
                marginTop: '0.25rem'
              }}>
                {customReason.length}/500 characters
              </div>
            </Section>
          )}
        </Content>
        <Actions>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button $variant="danger" onClick={handleConfirm} disabled={!isFormValid}>
            Reject Story
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};